import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  output,
  OutputEmitterRef,
  WritableSignal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EventType, Router, RouterLink } from '@angular/router';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { Subscription } from 'rxjs';
import { SectionComponent } from '../section/section.component';
import { ExtendableTextComponent } from '../extendable-text/extendable-text.component';
import { MenuSelectionSupportService } from '../../../services/support/menu-selection-support/menu-selection-support.service';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';

export interface ISideNavigationLink {
  label: string;
  url: string;
}

@Component({
  selector: 'aw-side-section-navigation',
  imports: [
    CommonModule,
    RouterLink,
    AppendTestIdPipe,
    SectionComponent,
    ExtendableTextComponent,
  ],
  templateUrl: './side-section-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideSectionNavigationComponent
  implements AfterViewInit, OnDestroy
{
  public id: InputSignal<string> = input.required();
  public icon: InputSignal<string> = input.required();
  public title: InputSignal<string> = input.required();
  public titleLink: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );
  public links: InputSignal<ISideNavigationLink[]> = input<
    ISideNavigationLink[]
  >([]);

  private readonly router: Router = inject(Router);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  public readonly menuSelectSupport: MenuSelectionSupportService =
    new MenuSelectionSupportService();
  public readonly activeLink: WritableSignal<ISideNavigationLink | undefined> =
    signal(undefined);
  public onSelect: OutputEmitterRef<boolean> = output<boolean>();

  private readonly subscriptions: Subscription = new Subscription();

  @ViewChild('extendableText')
  protected readonly extendableTextRef!: any;

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event.type === EventType.NavigationEnd) {
          this.activeLink.set(this.determineActiveLink(this.links()));
          this.setExtendedTextState();
        }
      })
    );
    this.activeLink.set(this.determineActiveLink(this.links()));
    this.setExtendedTextState();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public navigate(link: string): void {
    this.onSelect.emit(true);
    this.router.navigate([link]);
  }

  public determineActiveLink(
    links: ISideNavigationLink[]
  ): ISideNavigationLink | undefined {
    const urlSegments = this.router.url.split('/');
    const lastPath = urlSegments[urlSegments.length - 1];
    return links.find(link => link.url == lastPath);
  }

  private setExtendedTextState(): void {
    if (!this.activeLink()) {
      this.extendableTextRef.extended = false;
    } else {
      this.extendableTextRef.extended = true;
    }
  }

  protected determineLinkBackground(link: ISideNavigationLink): string {
    const isActiveLink = this.activeLink() === link;
    const isDarkMode = this.corporateDesignService.isDarkModeActive();
    const corporateDesign = this.corporateDesignService.corporateDesign;
    const opacity = '22';

    if (isActiveLink) {
      return isDarkMode
        ? this.corporateDesignService.corporateDesign().colorAdmixtures
            .dark_surface_end
        : corporateDesign().primaryColor + opacity;
    } else {
      return isDarkMode
        ? this.corporateDesignService.corporateDesign().colorAdmixtures
            .dark_surface_01
        : '';
    }
  }
}
